import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../../components/navbar/navbar';
import Footer from '../../components/footer';
import api, { API_BASE, getImageUrl } from '../../utils/api';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import GridProperty from '../../components/grid-property';

import Select from 'react-select';
import './project-details.css';

import streetViewIcon from "../../assets/img/street-view.png";
import vrGlassesIcon from "../../assets/img/vr-glasses.png";

// Import Section Components
import ProjectHero from './sections/ProjectHero';
import StickyTabs from './sections/StickyTabs';
import ProjectOverview from './sections/ProjectOverview';
import KeyHighlights from './sections/KeyHighlights';
import AboutProject from './sections/AboutProject';
import ProjectLocationInfo from './sections/ProjectLocationInfo';
import LocationMap from './sections/LocationMap';
import NearbyPlaces from './sections/NearbyPlaces';
import PriceTrendsSection from './sections/PriceTrendsSection';
import ProjectGallery from './sections/ProjectGallery';
import FloorPlansSection from './sections/FloorPlansSection';
import LegalDocumentsSection from './sections/LegalDocumentsSection';
import AmenitiesSection from './sections/AmenitiesSection';
import SpecificationsSection from './sections/SpecificationsSection';
import BuilderOffersSection from './sections/BuilderOffersSection';
import DeveloperSpotlight from './sections/DeveloperSpotlight';
import MediaTour from './sections/MediaTour';


import HelpfulTools from './sections/HelpfulTools';
import SimilarProjectsSection from './sections/SimilarProjectsSection';
import FAQSection from './sections/FAQSection';
import ReviewsSection from './sections/ReviewsSection';
import InquirySidebar from './sections/InquirySidebar';
import ScheduleTourSection from './sections/ScheduleTourSection';
import ScheduleTourSidebar from './sections/ScheduleTourSidebar';
import BrochureModal from './sections/BrochureModal';
import ComparePanel from './sections/ComparePanel';
import MorePropertiesSection from './sections/MorePropertiesSection';
import GallerySliderModal from './sections/GallerySliderModal';
import MarketTrendsSidebar from './sections/MarketTrendsSidebar';
import InquiryModal from './sections/InquiryModal';

interface Project {
    _id: string
    name: string
    listingType: string
    propertyType: string
    subCategory?: string
    projectStatus: string
    tagline?: string
    slug?: string
    location: string
    address: string
    city?: string
    state?: string
    country?: string
    pinCode?: string
    mapDetails?: {
        googleMapLocation?: string
        google3dViewLink?: string
        latitude?: string
        longitude?: string
    }
    developerDetails?: {
        name?: string
        coDeveloper?: string
        logo?: string
        website?: string
        description?: string
        totalProjects?: string
        projectsCompleted?: string
        projectsOngoing?: string
        projectsUpcoming?: string
        projectsSince?: string
    }
    overview?: {
        configuration?: string
        sizeRange?: string
        priceRange?: string
        avgPricePerSqFt?: string
        projectArea?: string
        openSpacePercentage?: string
        totalTowers?: string
        totalFloors?: string
        totalUnits?: string
        floors?: string
    }
    launchDate?: string
    possessionDate?: string
    constructionStatus?: string
    reraNumber?: string
    reraAuthority?: string
    reraLink?: string
    featuredImage?: string
    galleryIcons?: Record<string, string>
    gallery?: {
        exterior?: string[]
        interior?: string[]
        amenities?: string[]
        construction?: string[]
        [key: string]: string[] | undefined
    }
    builderOffers?: {
        offerType: string;
        example: string;
        priceImpact: string;
        startDate?: string;
        tillDate?: string;
    }[];
    videoUrl?: string
    virtualTours?: { title: string; link: string }[]
    virtualTour360?: string
    brochureUrl?: string
    shortDescription?: string
    description?: string
    locationAdvantages?: { name: string; distance: string }[]
    additionalDetails?: { label: string; value: string; icon: string }[]
    floorPlans?: {
        unitType: string
        subUnits: {
            subUnitType: string
            details: { label: string; value: string }[]
            image2D?: string
            image3D?: string
            image?: string
        }[]
    }[]
    amenities: { _id: string; name: string; icon: string }[]
    specifications?: { category: string; details: string; icon: string }[]
    developer?: { _id: string; name: string; avatar?: string; logo?: string; about?: string; role?: string }
    legalDocuments?: { title: string; image: string }[]
    status?: string
    priceRange?: string
    configuration?: string
    projectType?: string
    isOurPick?: boolean
    tpImage?: string;
    faqs?: { question: string; answer: string }[]
    disclaimer?: string;
    localityStats?: any;
    similarLocalities?: any[];
    builderTrustScore?: number;
    localityTrustScore?: number;
    loadingScore?: number;
    legalTrustScore?: number;
    amenitiesScore?: number;
    specificationsScore?: number;
    constructionStatusScore?: number;
    highlights?: { icon: string; text: string }[];
    sectionInfo?: {
        overview?: string
        amenities?: string
        specifications?: string
        location?: string
        floorPlans?: string
        gallery?: string
        developer?: string
        nearby?: string
    }
}

export default function ProjectDetails() {
    const { slug } = useParams();
    const [project, setProject] = useState<Project | null>(null);
    const [devProperties, setDevProperties] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [zoomedImage, setZoomedImage] = useState<string | null>(null);
    const [zoomScale, setZoomScale] = useState(1);

    // Gallery Slider State
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);
    const [galleryImages, setGalleryImages] = useState<string[]>([]);
    const [galleryInitialIndex, setGalleryInitialIndex] = useState(0);

    const openGallery = (images: string[], index: number) => {
        setGalleryImages(images);
        setGalleryInitialIndex(index);
        setIsGalleryOpen(true);
    };
    const [expandedFloorPlan, setExpandedFloorPlan] = useState<string | null>(null);
    const sliderRef = React.useRef<any>(null);
    const nearbySliderRef = React.useRef<HTMLDivElement>(null);

    // Compare Properties state
    const [isCompareOpen, setIsCompareOpen] = useState(false);
    const [compareList, setCompareList] = useState<Project[]>([]);

    // Reviews state
    const [reviews, setReviews] = useState<any[]>([]);
    const [showReviewForm, setShowReviewForm] = useState(false);
    const [reviewForm, setReviewForm] = useState({ name: '', email: '', rating: '', message: '' });
    const [submitting, setSubmitting] = useState(false);
    const [reviewSuccess, setReviewSuccess] = useState('');
    const [reviewError, setReviewError] = useState('');

    // Similar projects state
    const [allProjects, setAllProjects] = useState<Project[]>([]);

    // Tour Booking state
    const [tourDate, setTourDate] = useState<Date>(new Date());
    const [tourType, setTourType] = useState<'In Person' | 'Video Chat'>('In Person');
    const [tourForm, setTourForm] = useState({
        time: '',
        name: '',
        phone: '',
        email: '',
        message: ''
    });
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [tourSubmitting, setTourSubmitting] = useState(false);
    const [tourSuccess, setTourSuccess] = useState('');
    const [tourError, setTourError] = useState('');

    // Brochure Download state
    const [showBrochureModal, setShowBrochureModal] = useState(false);
    const [showInquiryModal, setShowInquiryModal] = useState(false);
    const [selectedOfferForInquiry, setSelectedOfferForInquiry] = useState<any>(null);
    const [brochureStep, setBrochureStep] = useState<'info' | 'otp'>('info');
    const [brochureForm, setBrochureForm] = useState({
        name: '',
        email: '',
        mobile: '',
        otp: ''
    });
    const [brochureSubmitting, setBrochureSubmitting] = useState(false);
    const [brochureError, setBrochureError] = useState('');

    // Inquiry Form state
    const [inquiryForm, setInquiryForm] = useState({
        name: '',
        email: '',
        phone: '',
        agreeContact: false,
        agreeTerms: false,
        interestedHomeLoan: true,
        purpose: 'Buy',
        propertyType: '',
        propertySubType: ''
    });
    const [inquirySubmitting, setInquirySubmitting] = useState(false);
    const [inquirySuccess, setInquirySuccess] = useState('');
    const [inquiryError, setInquiryError] = useState('');

    // Sticky Nav / Scroll-Spy State
    const [showStickyTabs, setShowStickyTabs] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
    const [isDisclaimerExpanded, setIsDisclaimerExpanded] = useState(false);

    useEffect(() => {
        const saved = localStorage.getItem('compareList');
        if (saved) {
            try {
                setCompareList(JSON.parse(saved));
            } catch (e) {
                console.error("Failed to parse compareList", e);
            }
        }
    }, []);

    useEffect(() => {
        localStorage.setItem('compareList', JSON.stringify(compareList));
    }, [compareList]);

    const addToCompare = (prop: any) => {
        if (!prop) return;
        const propId = prop._id || prop.id;
        if (!propId) return;

        const exists = compareList.find(p => (p._id || (p as any).id) === propId);
        if (exists) {
            setIsCompareOpen(true);
            return;
        }

        if (compareList.length >= 3) {
            alert("You can compare up to 3 properties at a time.");
            return;
        }

        setCompareList([...compareList, prop]);
        setIsCompareOpen(true);
    };

    const removeFromCompare = (id: string) => {
        setCompareList(compareList.filter(p => (p._id || (p as any).id) !== id));
    };

    const getLabelIcon = (label: string) => {
        const l = label.toLowerCase();
        if (l.includes('room') || l.includes('bedroom')) return <i className="fa-solid fa-bed text-blue-600"></i>;
        if (l.includes('balcony')) return <i className="fa-solid fa-couch text-blue-600"></i>;
        if (l.includes('bath')) return <i className="fa-solid fa-shower text-blue-600"></i>;
        if (l.includes('area') || l.includes('suba') || l.includes('carpet') || l.includes('built-up') || l.includes('builtup')) return <i className="fa-solid fa-vector-square text-blue-600"></i>;
        if (l.includes('price')) return <i className="fa-solid fa-indian-rupee-sign text-blue-600"></i>;
        return <i className="fa-solid fa-circle-info text-blue-600"></i>;
    };

    const scrollNearby = (direction: 'left' | 'right') => {
        if (nearbySliderRef.current) {
            const scrollAmount = 350;
            nearbySliderRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    const getDetailValue = (label: string) => {
        if (!project?.additionalDetails) return null;
        return project.additionalDetails.find(d => d.label.toLowerCase().includes(label.toLowerCase()))?.value;
    };

    const formatPossessionDate = (dateString: any) => {
        if (!dateString) return 'Ready to Move';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    };

    const handleBrochureOtpSend = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!brochureForm.name || !brochureForm.mobile) {
            setBrochureError('Name and Mobile number are required.');
            return;
        }
        setBrochureSubmitting(true);
        setBrochureError('');
        try {
            const res = await fetch(`${API_BASE}/leads/brochure/send-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: brochureForm.email })
            });
            const data = await res.json();
            if (res.ok) {
                setBrochureStep('otp');
            } else {
                setBrochureError(data.message || 'Failed to send OTP.');
            }
        } catch {
            setBrochureError('Something went wrong. Please try again.');
        } finally {
            setBrochureSubmitting(false);
        }
    };

    const handleBrochureVerify = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!brochureForm.otp) {
            setBrochureError('Please enter the OTP.');
            return;
        }
        setBrochureSubmitting(true);
        setBrochureError('');
        try {
            const res = await fetch(`${API_BASE}/leads/brochure/verify-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: brochureForm.email,
                    otp: brochureForm.otp,
                    name: brochureForm.name,
                    mobile: brochureForm.mobile,
                    projectId: project?._id || slug
                })
            });
            const data = await res.json();
            if (res.ok) {
                setShowBrochureModal(false);
                setBrochureStep('info');
                setBrochureForm({ name: '', email: '', mobile: '', otp: '' });
                // Trigger actual download
                if (project?.brochureUrl) {
                    const downloadUrl = getImageUrl(project.brochureUrl);
                    window.open(downloadUrl, '_blank');
                }
            } else {
                setBrochureError(data.message || 'Invalid OTP.');
            }
        } catch {
            setBrochureError('Verification failed. Please try again.');
        } finally {
            setBrochureSubmitting(false);
        }
    };

    const handleInquirySubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!inquiryForm.name || !inquiryForm.phone) {
            setInquiryError('Name and Phone number are required.');
            return;
        }
        if (!inquiryForm.agreeTerms) {
            setInquiryError('Please agree to the Terms & Conditions.');
            return;
        }
        setInquirySubmitting(true);
        setInquiryError('');
        setInquirySuccess('');
        try {
            const res = await fetch(`${API_BASE}/bookings`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    project: project?._id || slug,
                    type: 'Project Inquiry',
                    name: inquiryForm.name,
                    phone: inquiryForm.phone,
                    email: inquiryForm.email,
                    message: selectedOfferForInquiry
                        ? `CLAIM OFFER: ${selectedOfferForInquiry.example} (${selectedOfferForInquiry.offerType}) for ${project?.name || 'project'}`
                        : `General inquiry for ${project?.name || 'project'}${inquiryForm.interestedHomeLoan ? ' (Interested in Home Loans)' : ''}`,
                    agreeContact: inquiryForm.agreeContact,
                    interestedHomeLoan: inquiryForm.interestedHomeLoan,
                    purpose: inquiryForm.purpose || 'Buy',
                    propertyType: inquiryForm.propertyType || (Array.isArray(project?.propertyType) ? project?.propertyType.join(', ') : '') || 'Residential',
                    propertySubType: inquiryForm.propertySubType || (Array.isArray(project?.subCategory) ? project?.subCategory.join(', ') : '') || 'General'
                })
            });
            const data = await res.json();
            if (res.ok) {
                setInquirySuccess('Your inquiry has been submitted! We will contact you soon.');
                setInquiryForm({
                    name: '',
                    email: '',
                    phone: '',
                    agreeContact: false,
                    agreeTerms: false,
                    interestedHomeLoan: true,
                    purpose: 'Buy',
                    propertyType: '',
                    propertySubType: ''
                });
                setTimeout(() => setInquirySuccess(''), 5000);
            } else {
                setInquiryError(data.message || 'Failed to submit inquiry.');
            }
        } catch {
            setInquiryError('Something went wrong. Please try again.');
        } finally {
            setInquirySubmitting(false);
        }
    };

    const generateTourDates = () => {
        const dates = [];
        const today = new Date();
        for (let i = 0; i < 15; i++) {
            const d = new Date(today);
            d.setDate(today.getDate() + i);
            dates.push(d);
        }
        return dates;
    };

    const timeSlots = [
        "09:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
        "01:00 PM", "02:00 PM", "03:00 PM", "04:00 PM",
        "05:00 PM", "06:00 PM"
    ];

    const getAvailableTimeSlots = () => {
        const now = new Date();
        const isToday = tourDate.toDateString() === now.toDateString();

        if (!isToday) return timeSlots;

        return timeSlots.filter(slot => {
            const [time, modifier] = slot.split(' ');
            let [hours, minutes] = time.split(':').map(Number);

            if (modifier === 'PM' && hours < 12) hours += 12;
            if (modifier === 'AM' && hours === 12) hours = 0;

            const slotDate = new Date();
            slotDate.setHours(hours, minutes, 0, 0);

            return slotDate > now;
        });
    };

    const handleTourSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!tourForm.time || !tourForm.name || !tourForm.phone || !tourForm.email) {
            setTourError('Please fill all required fields.');
            return;
        }
        if (!termsAccepted) {
            setTourError('Please accept the Terms of Use to proceed.');
            return;
        }
        setTourSubmitting(true);
        setTourError('');
        setTourSuccess('');

        try {
            const res = await fetch(`${API_BASE}/bookings`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    project: project?._id || slug,
                    type: 'Site Visit',
                    name: tourForm.name,
                    phone: tourForm.phone,
                    email: tourForm.email,
                    date: tourDate.toISOString(),
                    time: tourForm.time,
                    message: tourForm.message || `Tour request for ${project?.name || 'project'}`
                })
            });
            const data = await res.json();
            if (res.ok) {
                setTourSuccess('Your tour has been scheduled successfully! We will contact you shortly.');
                setTourForm({ time: '', name: '', phone: '', email: '', message: '' });
                setTermsAccepted(false);
                setTimeout(() => setTourSuccess(''), 5000);
            } else {
                setTourError(data.message || 'Failed to submit. Please try again.');
            }
        } catch {
            setTourError('Something went wrong. Please try again later.');
        } finally {
            setTourSubmitting(false);
        }
    };

    useEffect(() => {
        if (slug) {
            api.getProjectById(slug)
                .then(data => {
                    if (data._id) {
                        setProject(data);
                        // Fetch developer's full profile to get properties
                        if (data.developer?._id) {
                            const isBroker = data.developer.role === 'broker';
                            const url = isBroker 
                                ? `${API_BASE}/users/agents/${data.developer._id}` 
                                : `${API_BASE}/developers/${data.developer._id}`;
                            fetch(url)
                                .then(res => res.json())
                                .then(devData => {
                                    if (devData.properties) {
                                        setDevProperties(devData.properties);
                                    }
                                })
                                .catch(err => console.error('Error fetching developer/broker properties:', err));
                        }

                        // Fetch similar projects efficiently
                        const queryParams = new URLSearchParams();
                        if (data.propertyType) queryParams.append('propertyType', data.propertyType);
                        queryParams.append('limit', '10');

                        fetch(`${API_BASE}/projects?${queryParams.toString()}`)
                            .then(res => res.json())
                            .then(similarData => {
                                if (Array.isArray(similarData)) setAllProjects(similarData)
                            })
                            .catch(err => console.error('Failed to fetch similar projects', err));
                    }
                    else setError('Project not found');
                })
                .catch(err => setError('Failed to load project details'))
                .finally(() => setLoading(false));

            // Fetch reviews
            fetch(`${API_BASE}/reviews/${slug}`)
                .then(res => res.json())
                .then(data => { if (Array.isArray(data)) setReviews(data) })
                .catch(err => console.error('Failed to fetch reviews', err));
        }
    }, [slug]);

    useEffect(() => {
        const handleScroll = () => {
            const tabsTrigger = document.getElementById('project-tabs-trigger');
            if (tabsTrigger) {
                if (tabsTrigger.getBoundingClientRect().top <= 2) {
                    setShowStickyTabs(true);
                } else {
                    setShowStickyTabs(false);
                }
            } else {
                if (window.scrollY > 300) {
                    setShowStickyTabs(true);
                } else {
                    setShowStickyTabs(false);
                }
            }
        };

        window.addEventListener('scroll', handleScroll);

        // Scroll Spy Logic
        const sections = [
            'overview', 'key-highlights', 'developer-details', 'about-project', 'location-details', 'project-location', 'nearby-landmarks', 'gallery', 'media-tour', 'price-trends', 'floor-plans', 'legal-documents', 'amenities', 'specifications', 'faq-section', 'reviews'
        ];

        const observerOptions = {
            root: null,
            rootMargin: '-145px 0px -70% 0px',
            threshold: 0
        };

        const observerCallback = (entries: IntersectionObserverEntry[]) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    setActiveTab(entry.target.id);
                }
            });
        };

        const observer = new IntersectionObserver(observerCallback, observerOptions);
        sections.forEach(id => {
            const el = document.getElementById(id);
            if (el) observer.observe(el);
        });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            observer.disconnect();
        };
    }, [loading]);

    const scrollToSection = (sectionId: string) => {
        const el = document.getElementById(sectionId);
        if (el) {
            const offset = 145;
            const bodyRect = document.body.getBoundingClientRect().top;
            const elementRect = el.getBoundingClientRect().top;
            const elementPosition = elementRect - bodyRect;
            const offsetPosition = elementPosition - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    const handleReviewSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!reviewForm.name || !reviewForm.email || !reviewForm.rating || !reviewForm.message) {
            setReviewError('Please fill all fields.'); return;
        }
        setSubmitting(true); setReviewError(''); setReviewSuccess('');
        try {
            const res = await fetch(`${API_BASE}/reviews`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ property: project?._id || slug, ...reviewForm, rating: Number(reviewForm.rating) })
            });
            const data = await res.json();
            if (res.ok) {
                setReviews(prev => [data, ...prev]);
                setReviewForm({ name: '', email: '', rating: '', message: '' });
                setShowReviewForm(false);
                setReviewSuccess('Your review has been submitted successfully!');
                setTimeout(() => setReviewSuccess(''), 4000);
            } else {
                setReviewError(data.message || 'Failed to submit review.');
            }
        } catch {
            setReviewError('Server error. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const renderStars = (count: number) => {
        const stars = [];
        for (let i = 1; i <= 5; i++) {
            stars.push(
                <i key={i} className={`fa-star ${i <= count ? 'fa-solid text-accent' : 'fa-regular text-gray-500'}`}
                    style={{ fontSize: '16px', marginRight: '2px' }}></i>
            );
        }
        return stars;
    };

    const ratingOptions = [
        { value: '1', label: '⭐ 1 Star' },
        { value: '2', label: '⭐⭐ 2 Stars' },
        { value: '3', label: '⭐⭐⭐ 3 Stars' },
        { value: '4', label: '⭐⭐⭐⭐ 4 Stars' },
        { value: '5', label: '⭐⭐⭐⭐⭐ 5 Stars' },
    ];

    const similarSliderSettings = {
        dots: false,
        infinite: false,
        speed: 500,
        slidesToShow: 2.5,
        slidesToScroll: 1,
        autoplay: false,
        arrows: false,
        responsive: [
            { breakpoint: 1024, settings: { slidesToShow: 2, slidesToScroll: 1 } },
            { breakpoint: 768, settings: { slidesToShow: 1.2, slidesToScroll: 1 } }
        ]
    };

    const similarProjects = allProjects.filter(p =>
        project && p._id !== project._id && p.slug !== slug &&
        (p.propertyType === project.propertyType || p.listingType === project.listingType)
    ).slice(0, 6);



    const sliderSettings = {
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 3000,
        arrows: true
    };

    if (loading) return (
        <>
            <Navbar transparent={false} />
            <div className="loading-container mx-auto px-6 text-center py-8 my-5">
                <div className="spinner-border-gray-200 text-primary" role="status"></div>
                <p className="mt-6 text-gray-500 font-medium">Bringing you project details...</p>
            </div>
            <Footer />
        </>
    );

    if (error || !project) return (
        <>
            <Navbar transparent={false} />
            <div className="error-container mx-auto px-6 text-center py-8 my-5">
                <i className="fa-solid fa-circle-exclamation text-red-500 fa-4x mb-6"></i>
                <h3>Oops! {error || 'Something went wrong'}</h3>
                <Link to="/" className="inline-block text-center cursor-pointer transition-all btn-primary mt-6">Back to Home</Link>
            </div>
            <Footer />
        </>
    );

    return (
        <div className="project-detail-page bg-gray-50">
            <Navbar transparent={false} forceHide={showStickyTabs} />

            <ProjectHero
                project={project}
                getImageUrl={getImageUrl}
                scrollToSection={scrollToSection}
                sliderSettings={sliderSettings}
                openGallery={openGallery}
                addToCompare={addToCompare}
            />

            {/* Project Thumbnails Strip - Below Hero Section */}
            {project && (
                <div className="bg-white border-b border-gray-200">
                    <div className="pl-1 md:pl-2 py-1">
                        <div className="flex gap-2 overflow-x-auto no-scrollbar py-1 w-full text-left">
                            {[
                                ...(project.featuredImage ? [project.featuredImage] : []),
                                ...(project.tpImage ? [project.tpImage] : []),
                                ...Object.values(project.gallery || {}).flat()
                            ].filter((img, index, self): img is string => !!img && self.indexOf(img) === index)
                                .slice(0, 20).map((img, i, arr) => (
                                    <div
                                        key={i}
                                        onClick={() => openGallery(arr, i)}
                                        className="flex-shrink-0 cursor-pointer rounded-lg overflow-hidden border-2 border-gray-100 transition-all hover:border-primary hover:shadow-lg relative group"
                                        style={{
                                            width: '100px',
                                            height: '70px',
                                            backgroundImage: `url(${getImageUrl(img)})`,
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center',
                                            minWidth: '100px'
                                        }}
                                    >
                                        <div className="absolute inset-0 bg-black/5 group-hover:bg-transparent transition-colors"></div>
                                        {i === 19 && arr.length > 20 && (
                                            <div className="absolute inset-0 bg-black/70 backdrop-blur-[2px] flex flex-col items-center justify-center text-white">
                                                <span className="text-xl font-black">+{arr.length - 19}</span>
                                                <span className="text-[10px] font-bold uppercase tracking-widest mt-1">More View</span>
                                            </div>
                                        )}
                                    </div>
                                ))}
                        </div>
                    </div>
                </div>
            )}

            <div className="container mx-auto px-4 md:hidden mt-2">
                {/* Mobile Utility Icons Bar */}
                {project && (
                    <div className="bg-white mb-2 flex gap-2 items-center justify-center overflow-x-auto no-scrollbar py-0.5">
                        <button onClick={() => scrollToSection('gallery')} className="w-12 h-12 flex items-center justify-center bg-primary text-white rounded-xl shadow-md border border-primary/20 shrink-0 transition-transform active:scale-90"><i className="fa-regular fa-image text-lg"></i></button>

                        <button onClick={() => scrollToSection('location-map')} className="w-12 h-12 flex items-center justify-center bg-slate-800 text-white rounded-xl shadow-md border border-white/10 shrink-0 transition-transform active:scale-90"><i className="fa-solid fa-map text-lg"></i></button>

                        <a
                            href={project.mapDetails?.google3dViewLink || "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-12 h-12 flex items-center justify-center bg-slate-800 text-white rounded-xl shadow-md border border-white/10 shrink-0 no-underline transition-transform active:scale-90"
                            onClick={(e) => {
                                if (!project.mapDetails?.google3dViewLink) {
                                    e.preventDefault();
                                    alert("3D View not available yet.");
                                }
                            }}
                        >
                            <img src={streetViewIcon} alt="Street View" className="w-6 h-6 object-contain brightness-0 invert" />
                        </a>

                        <button onClick={() => scrollToSection('media-tour')} className="w-12 h-12 flex items-center justify-center bg-slate-800 text-white rounded-xl shadow-md border border-white/10 shrink-0 transition-transform active:scale-90">
                            <img src={vrGlassesIcon} alt="VR" className="w-9 h-9 object-contain brightness-0 invert" />
                        </button>

                        <button onClick={() => scrollToSection('media-tour')} className="w-12 h-12 flex items-center justify-center bg-slate-800 text-white rounded-xl shadow-md border border-white/10 shrink-0 transition-transform active:scale-90"><i className="fa-solid fa-video text-lg"></i></button>

                        {/* Add to Compare for Mobile */}
                        <button
                            onClick={() => addToCompare(project)}
                            className="w-12 h-12 flex items-center justify-center bg-slate-800 text-white rounded-xl shadow-md border border-white/10 shrink-0 transition-transform active:scale-95"
                            title="Add to Compare"
                        >
                            <i className="fa-solid fa-plus-square text-lg"></i>
                        </button>
                    </div>
                )}

                {/* Project Mobile Header */}
                {project && (
                    <div className="bg-transparent px-1 py-1 mb-2">
                        <div className="flex flex-wrap items-center gap-3 mb-2 px-1">
                            <nav className="flex items-center gap-1.5 text-black text-[12px] font-bold">
                                <Link to="/" className="hover:text-black transition-colors">Home</Link>
                                <i className="fa-solid fa-chevron-right text-[7px] opacity-30"></i>
                                <span>{project.propertyType || 'Prop'}</span>
                            </nav>
                            <div className="flex items-center gap-2">
                                <div className="bg-slate-100 px-3 py-1 rounded font-black text-black text-[9px] tracking-widest uppercase border border-slate-200">
                                    ID: {project._id?.substring(project._id.length - 4).toUpperCase()}
                                </div>
                                <span className="inline-flex items-center gap-1 bg-white px-2 py-0.5 rounded border border-gray-200 shadow-sm">
                                    <i className="fa-solid fa-check text-emerald-500 text-[8px]"></i>
                                    <span className="text-[8px] font-bold text-slate-800 tracking-wide">RERA</span>
                                </span>
                            </div>
                        </div>

                        <h1 className="text-3xl font-bold text-black mb-1 leading-tight tracking-tight capitalize text-left px-1">{project.name}</h1>
                        {project.developer?.name && (
                            <div className="text-[12px] md:text-[13px] font-bold text-black mb-2 text-left px-1 flex items-center gap-1">
                                <span className="opacity-60">By</span>
                                <Link 
                                    to={project.developer?.role === 'owner' ? `/owner-profile/${project.developer._id}` : project.developer?.role === 'broker' ? `/agent-page/${project.developer._id}` : `/developer-page/${project.developer._id}`} 
                                    className="text-primary hover:text-navy transition-colors no-underline"
                                >
                                    {project.developer.name}
                                </Link>
                            </div>
                        )}

                        <div className="flex items-start gap-2.5 text-black text-[13px] leading-relaxed text-left px-1">
                            <div className="w-6 h-6 flex items-center justify-center bg-slate-100 rounded shrink-0 mt-0.5 shadow-sm border border-slate-200">
                                <i className="fa-solid fa-location-dot text-[12px]"></i>
                            </div>
                            <span className="font-bold capitalize">{project.address || project.location}</span>
                        </div>
                    </div>
                )}
            </div>

            <section className="pb-1 pt-0 relative z-20">
                <StickyTabs
                    showStickyTabs={showStickyTabs}
                    activeTab={activeTab}
                    scrollToSection={scrollToSection}
                    project={project}
                />
                <div className="container-fluid px-3 px-md-5">
                    <div className="flex flex-wrap -mx-3 items-stretch overflow-visible">
                        {/* Main Content */}
                        <div className="w-full lg:w-[70%] pl-3 pr-4 pt-0 md:pt-4 lg:pt-6">



                            <ProjectOverview project={project} formatPossessionDate={formatPossessionDate} setShowBrochureModal={setShowBrochureModal} />
                            <KeyHighlights highlights={project.highlights} sectionInfo={project.sectionInfo} getImageUrl={getImageUrl} />
                            <BuilderOffersSection
                                project={project}
                                onClaimOffer={(offer) => {
                                    setSelectedOfferForInquiry(offer);
                                    setShowInquiryModal(true);
                                }}
                            />
                            <DeveloperSpotlight
                                project={project}
                                getImageUrl={getImageUrl}
                            />
                            <AboutProject project={project} isDescriptionExpanded={isDescriptionExpanded} setIsDescriptionExpanded={setIsDescriptionExpanded} />
                            <FloorPlansSection
                                project={project}
                                expandedFloorPlan={expandedFloorPlan}
                                setExpandedFloorPlan={setExpandedFloorPlan}
                                getLabelIcon={getLabelIcon}
                                getImageUrl={getImageUrl}
                                setZoomedImage={setZoomedImage}
                            />
                            <ProjectLocationInfo project={project} />
                            <LocationMap project={project} />
                            <NearbyPlaces project={project} />

                            <ProjectGallery
                                project={project}
                                getImageUrl={getImageUrl}
                                openGallery={openGallery}
                            />
                            <MediaTour project={project} />



                            <LegalDocumentsSection
                                project={project}
                                getImageUrl={getImageUrl}
                                setZoomedImage={setZoomedImage}
                            />
                            <AmenitiesSection
                                project={project}
                                getImageUrl={getImageUrl}
                            />
                            <SpecificationsSection
                                project={project}
                                getImageUrl={getImageUrl}
                            />
                            <PriceTrendsSection
                                project={project}
                                locationStats={project.localityStats}
                                similarLocalities={project.similarLocalities || []}
                            />


                            <FAQSection project={project} />
                            <ReviewsSection
                                reviews={reviews}
                                renderStars={renderStars}
                                project={project}
                                showReviewForm={showReviewForm}
                                setShowReviewForm={setShowReviewForm}
                                reviewSuccess={reviewSuccess}
                                reviewError={reviewError}
                                handleReviewSubmit={handleReviewSubmit}
                                ratingOptions={ratingOptions}
                                reviewForm={reviewForm}
                                setReviewForm={setReviewForm}
                                submitting={submitting}
                            />

                            {/* Schedule A Tour Section */}
                            <ScheduleTourSection
                                project={project}
                                tourDate={tourDate}
                                setTourDate={setTourDate}
                                tourForm={tourForm}
                                setTourForm={setTourForm}
                                handleTourSubmit={handleTourSubmit}
                                tourSubmitting={tourSubmitting}
                                tourError={tourError}
                                tourSuccess={tourSuccess}
                                termsAccepted={termsAccepted}
                                setTermsAccepted={setTermsAccepted}
                                generateTourDates={generateTourDates}
                                tourType={tourType}
                                setTourType={setTourType}
                                getImageUrl={getImageUrl}
                            />

                            {/* Disclaimer Section */}
                            {project.disclaimer && (
                                <div id="disclaimer" className="mb-8 mt-6">
                                    <div className="p-6 md:p-8 rounded-3xl bg-gray-50 border border-gray-100 shadow-sm relative overflow-hidden">
                                        <div className="absolute top-0 left-0 w-1.5 h-full bg-amber-400"></div>
                                        <div className="flex items-start gap-4">
                                            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600 flex-shrink-0 mt-1">
                                                <i className="fa-solid fa-circle-exclamation text-lg"></i>
                                            </div>
                                            <div className="flex-1">
                                                <h5 className="font-bold text-black mb-2 text-[14px]">Disclaimer</h5>
                                                <div className="relative">
                                                    <p
                                                        className={`text-black text-[12px] md:text-[14px] leading-relaxed mb-0 whitespace-pre-line font-medium transition-all duration-300 ${!isDisclaimerExpanded ? 'line-clamp-7 overflow-hidden' : ''}`}
                                                        style={!isDisclaimerExpanded ? {
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 7,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden'
                                                        } : {}}
                                                    >
                                                        {project.disclaimer}
                                                    </p>
                                                    <button
                                                        onClick={() => setIsDisclaimerExpanded(!isDisclaimerExpanded)}
                                                        className="mt-3 text-[#f1ae24] font-bold text-xs md:text-sm hover:underline flex items-center gap-2"
                                                    >
                                                        {isDisclaimerExpanded ? (
                                                            <>Read Less <i className="fa-solid fa-chevron-up text-[10px]"></i></>
                                                        ) : (
                                                            <>Read More <i className="fa-solid fa-chevron-down text-[10px]"></i></>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Sidebar */}
                        <div className="w-full lg:w-[30%] pl-4 pr-3 pt-2 md:pt-4 lg:pt-0">
                            {/* Sticky Sidebar Container */}
                            <div id="inquiry-section" className="lg:sticky lg:top-[75px] z-[80] space-y-8">
                                <InquirySidebar
                                    project={project}
                                    scrollToSection={scrollToSection}
                                    setShowBrochureModal={setShowBrochureModal}
                                    inquiryForm={inquiryForm}
                                    setInquiryForm={setInquiryForm}
                                    handleInquirySubmit={handleInquirySubmit}
                                    inquirySubmitting={inquirySubmitting}
                                    inquiryError={inquiryError}
                                    inquirySuccess={inquirySuccess}
                                />

                                <ScheduleTourSidebar
                                    project={project}
                                    tourDate={tourDate}
                                    setTourDate={setTourDate}
                                    tourForm={tourForm}
                                    setTourForm={setTourForm}
                                    handleTourSubmit={handleTourSubmit}
                                    tourSubmitting={tourSubmitting}
                                    tourError={tourError}
                                    tourSuccess={tourSuccess}
                                    termsAccepted={termsAccepted}
                                    setTermsAccepted={setTermsAccepted}
                                    generateTourDates={generateTourDates}
                                    tourType={tourType}
                                    setTourType={setTourType}
                                />

                                <HelpfulTools
                                    project={project}
                                    addToCompare={addToCompare}
                                    compareList={compareList}
                                    scrollToSection={scrollToSection}
                                />
                            </div>
                        </div>
                    </div>

                    <MorePropertiesSection
                        project={project}
                        devProperties={devProperties}
                    />

                    <SimilarProjectsSection
                        similarProjects={similarProjects}
                        sliderRef={sliderRef}
                        similarSliderSettings={similarSliderSettings}
                        getImageUrl={getImageUrl}
                        addToCompare={addToCompare}
                        compareList={compareList}
                    />
                </div>
            </section>

            <Footer />

            <BrochureModal
                showBrochureModal={showBrochureModal}
                setShowBrochureModal={setShowBrochureModal}
                brochureStep={brochureStep}
                setBrochureStep={setBrochureStep}
                brochureForm={brochureForm}
                setBrochureForm={setBrochureForm}
                handleBrochureOtpSend={handleBrochureOtpSend}
                handleBrochureVerify={handleBrochureVerify}
                brochureSubmitting={brochureSubmitting}
                brochureError={brochureError}
                setBrochureError={setBrochureError}
            />

            <GallerySliderModal
                isOpen={isGalleryOpen}
                initialIndex={galleryInitialIndex}
                images={galleryImages}
                onClose={() => setIsGalleryOpen(false)}
                getImageUrl={getImageUrl}
            />

            {isCompareOpen && (
                <ComparePanel
                    isCompareOpen={isCompareOpen}
                    setIsCompareOpen={setIsCompareOpen}
                    compareList={compareList}
                    removeFromCompare={removeFromCompare}
                    getImageUrl={getImageUrl}
                />
            )}

            <InquiryModal
                show={showInquiryModal}
                onClose={() => {
                    setShowInquiryModal(false);
                    setSelectedOfferForInquiry(null);
                    setInquiryError('');
                    setInquirySuccess('');
                }}
                project={project}
                offer={selectedOfferForInquiry}
                handleInquirySubmit={handleInquirySubmit}
                inquiryForm={inquiryForm}
                setInquiryForm={setInquiryForm}
                inquirySubmitting={inquirySubmitting}
                inquirySuccess={inquirySuccess}
                inquiryError={inquiryError}
            />


            {zoomedImage && (
                <div
                    className="fixed inset-0 z-[2000] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-8 cursor-zoom-out"
                    onClick={() => setZoomedImage(null)}
                >
                    <button
                        className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full transition-all"
                        onClick={() => setZoomedImage(null)}
                    >
                        <i className="fa-solid fa-xmark text-2xl"></i>
                    </button>
                    <div className="relative max-w-5xl w-full h-full flex items-center justify-center">
                        <img
                            src={zoomedImage}
                            className="max-w-full max-h-full object-contain shadow-2xl rounded-lg transition-transform duration-500 scale-100"
                            alt="Zoomed View"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                </div>
            )}

        </div>
    );
}